/**
 * VIB-726: Admin-tabell med alle timebank-transaksjoner.
 *
 * Filter på bruker, type, status og dato-intervall. Paginering 50/side.
 * Sletteknapp på approved/rejected rader åpner cancel-dialog.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { formatDate, formatHours } from "@/lib/format";
import { timebankStatusLabel, timebankStatusStyle } from "@/lib/timebank-status";
import {
  useAllTimebankTransactions,
  type TimebankFilters,
  type TimebankTransaction,
} from "@/hooks/use-timebank";
import { useUsers } from "@/hooks/use-users";
import { CancelTransactionDialog } from "./CancelTransactionDialog";


const KIND_LABELS: Record<string, string> = {
  deposit: "Oppsparing",
  withdrawal: "Uttak",
};

/**
 * VIB-1286: «Gjelder»-kolonnen — skiller søkt-dato (created_at, vist i egen
 * kolonne) fra datoen transaksjonen FAKTISK gjelder:
 * - Oppsparing: timeføringens dato (via join på time_entry_id) — når timene
 *   ble opparbeidet, kan avvike fra søknadsdatoen.
 * - Uttak: dato-range + valgfritt klokkeslett.
 * Rader uten koblingsdata (eldre oppsparing uten time_entry_id) viser «—».
 */
function formatAppliesTo(tx: TimebankTransaction): string {
  if (tx.kind === "deposit") {
    return tx.time_entry?.start_date ? formatDate(tx.time_entry.start_date) : "—";
  }
  if (!tx.start_date || !tx.end_date) return "—";
  const start = `${formatDate(tx.start_date)}${tx.start_time ? ` ${tx.start_time.slice(0, 5)}` : ""}`;
  const end = `${formatDate(tx.end_date)}${tx.end_time ? ` ${tx.end_time.slice(0, 5)}` : ""}`;
  return tx.start_date === tx.end_date && !tx.start_time && !tx.end_time ? start : `${start} – ${end}`;
}

export function AllTransactionsAdminTable() {
  const [filters, setFilters] = useState<TimebankFilters>({ pageSize: 50 });
  const [cancelingTx, setCancelingTx] = useState<TimebankTransaction | null>(null);
  const { data, isLoading } = useAllTimebankTransactions(filters);
  const { data: users = [] } = useUsers();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageSize = filters.pageSize ?? 50;
  const page = filters.page ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const updateFilter = <K extends keyof TimebankFilters>(
    key: K,
    value: TimebankFilters[K],
  ) => {
    setFilters({ ...filters, [key]: value, page: 0 });
  };

  return (
    <div className="border-[1.5px] border-foreground bg-card rounded-md">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2 mb-3">
          <SrIcon name="filter" className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-lg">Alle transaksjoner</h3>
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {total} treff
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Select
            value={filters.userId ?? "all"}
            onValueChange={(v) => updateFilter("userId", v === "all" ? null : v)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Bruker" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle brukere</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.kind ?? "all"}
            onValueChange={(v) =>
              updateFilter(
                "kind",
                v === "all" ? null : (v as "deposit" | "withdrawal"),
              )
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle typer</SelectItem>
              <SelectItem value="deposit">Oppsparing</SelectItem>
              <SelectItem value="withdrawal">Uttak</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.status ?? "all"}
            onValueChange={(v) =>
              updateFilter(
                "status",
                v === "all"
                  ? null
                  : (v as "pending" | "approved" | "rejected" | "cancelled"),
              )
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statuser</SelectItem>
              <SelectItem value="pending">Venter</SelectItem>
              <SelectItem value="approved">Godkjent</SelectItem>
              <SelectItem value="rejected">Avslått</SelectItem>
              <SelectItem value="cancelled">Fjernet</SelectItem>
            </SelectContent>
          </Select>
          <input
            type="date"
            value={filters.fromDate ?? ""}
            onChange={(e) => updateFilter("fromDate", e.target.value || null)}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            placeholder="Fra"
          />
          <input
            type="date"
            value={filters.toDate ?? ""}
            onChange={(e) => updateFilter("toDate", e.target.value || null)}
            className="h-8 rounded-md border bg-background px-2 text-xs"
            placeholder="Til"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Ingen transaksjoner matcher filtrene.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bruker</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Timer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Søkt</TableHead>
              <TableHead>Gjelder</TableHead>
              <TableHead>Godkjent av</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((tx) => (
              <TableRow key={tx.id}>
                <TableCell className="text-sm font-medium">
                  {tx.user?.full_name ?? "Ukjent"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {KIND_LABELS[tx.kind] ?? tx.kind}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {tx.kind === "deposit" ? "+" : "−"}
                  {formatHours(tx.hours)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="secondary"
                    className={cn("text-[10px]", timebankStatusStyle(tx.status))}
                  >
                    {timebankStatusLabel(tx.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatDate(tx.created_at)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatAppliesTo(tx)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {tx.decider?.full_name ?? "—"}
                </TableCell>
                <TableCell className="text-right">
                  {(tx.status === "approved" || tx.status === "rejected") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs px-2 text-red-600 hover:bg-red-50"
                      onClick={() => setCancelingTx(tx)}
                      title="Fjern transaksjon"
                    >
                      <SrIcon name="trash-2" className="w-3 h-3 mr-1" />
                      Fjern
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between p-3 border-t text-xs">
          <span className="text-muted-foreground">
            Side {page + 1} av {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setFilters({ ...filters, page: page - 1 })}
            >
              Forrige
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages - 1}
              onClick={() => setFilters({ ...filters, page: page + 1 })}
            >
              Neste
            </Button>
          </div>
        </div>
      )}

      <CancelTransactionDialog
        transaction={cancelingTx}
        onClose={() => setCancelingTx(null)}
      />
    </div>
  );
}
